import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import "@/App.css";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Members from "@/pages/Members";
import MemberDetail from "@/pages/MemberDetail";
import ImportWizard from "@/pages/ImportWizard";
import Reconciliation from "@/pages/Reconciliation";
import AuditLog from "@/pages/AuditLog";
import MemberPortal from "@/pages/MemberPortal";
import Meetings from "@/pages/Meetings";
import MeetingDetail from "@/pages/MeetingDetail";
import PublicRsvp from "@/pages/PublicRsvp";
import LiveCheckin from "@/pages/LiveCheckin";
import MembershipForm from "@/pages/MembershipForm";
import Applications from "@/pages/Applications";
import Voting from "@/pages/Voting";
import PollDetail from "@/pages/PollDetail";
import PublicVote from "@/pages/PublicVote";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <Loader2 className="animate-spin text-brass" size={28} />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "member" ? "/portal" : "/"} replace />;
  }
  return children;
}

function AppRoutes() {
  const STAFF = ["admin", "secretariat"];
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/rsvp/:token" element={<PublicRsvp />} />
      <Route path="/checkin/:token" element={<LiveCheckin />} />
      <Route path="/apply" element={<MembershipForm />} />
      <Route path="/vote/:token" element={<PublicVote />} />
      <Route path="/" element={<Protected roles={STAFF}><Dashboard /></Protected>} />
      <Route path="/members" element={<Protected roles={STAFF}><Members /></Protected>} />
      <Route path="/members/:id" element={<Protected><MemberDetail /></Protected>} />
      <Route path="/applications" element={<Protected roles={STAFF}><Applications /></Protected>} />
      <Route path="/meetings" element={<Protected roles={STAFF}><Meetings /></Protected>} />
      <Route path="/meetings/:id" element={<Protected roles={STAFF}><MeetingDetail /></Protected>} />
      <Route path="/voting" element={<Protected roles={STAFF}><Voting /></Protected>} />
      <Route path="/voting/:id" element={<Protected roles={STAFF}><PollDetail /></Protected>} />
      <Route path="/import" element={<Protected roles={STAFF}><ImportWizard /></Protected>} />
      <Route path="/reconciliation" element={<Protected roles={STAFF}><Reconciliation /></Protected>} />
      <Route path="/audit" element={<Protected roles={["admin"]}><AuditLog /></Protected>} />
      <Route path="/portal" element={<Protected roles={["member"]}><MemberPortal /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
